import { privateApi } from '@/config/axios.config';

const API_BASE = '/condominium/payments';

export type PaymentMethod = 'cash' | 'bank_transfer' | 'check' | 'credit_card' | 'debit_card' | 'paypal' | 'mercado_pago' | 'azul' | 'carnet' | 'stripe';
export type PaymentStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded';

export interface Payment {
  id: string;
  invoiceId: string;
  invoiceNumber?: string;
  amount: number;
  method: PaymentMethod;
  status: PaymentStatus;
  paymentDate: string;
  reference?: string;
  transactionId?: string;
  notes?: string;
  receiptUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentCreateDTO {
  invoiceId: string;
  amount: number;
  method: PaymentMethod;
  paymentDate: string;
  reference?: string;
  transactionId?: string;
  notes?: string;
  status?: PaymentStatus;
  receiptUrl?: string;
  metadata?: Record<string, any>;
}

export interface PaymentUpdateDTO {
  amount?: number;
  method?: PaymentMethod;
  paymentDate?: string;
  reference?: string | null;
  transactionId?: string | null;
  notes?: string | null;
  status?: PaymentStatus;
  receiptUrl?: string | null;
  metadata?: Record<string, any>;
}

export interface PaymentFilters {
  invoiceId?: string;
  residentId?: string;
  status?: PaymentStatus;
  method?: PaymentMethod;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export const paymentsApiService = {
  // Obtener todos los pagos con filtros
  async getPayments(filters: PaymentFilters = {}): Promise<{
    payments: Payment[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();

    if (filters.invoiceId) params.append('invoiceId', filters.invoiceId);
    if (filters.residentId) params.append('residentId', filters.residentId);
    if (filters.status) params.append('status', filters.status);
    if (filters.method) params.append('method', filters.method);
    if (filters.search) params.append('search', filters.search);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await privateApi.get(`${API_BASE}?${params.toString()}`);
    return response.data?.data || response.data;
  },

  // Obtener un pago por ID
  async getPaymentById(id: string): Promise<Payment> {
    const response = await privateApi.get(`${API_BASE}/${id}`);
    return response.data?.data || response.data;
  },

  // Obtener pagos de una factura
  async getPaymentsByInvoice(invoiceId: string): Promise<Payment[]> {
    const response = await privateApi.get(`${API_BASE}/invoice/${invoiceId}`);
    return response.data?.data || response.data || [];
  },

  // Crear nuevo pago
  async createPayment(paymentData: PaymentCreateDTO): Promise<Payment> {
    const response = await privateApi.post(`${API_BASE}`, paymentData);
    return response.data?.data || response.data;
  },

  // Actualizar pago
  async updatePayment(id: string, paymentData: PaymentUpdateDTO): Promise<Payment> {
    const response = await privateApi.patch(`${API_BASE}/${id}`, paymentData);
    return response.data?.data || response.data;
  },

  // Eliminar pago
  async deletePayment(id: string): Promise<void> {
    await privateApi.delete(`${API_BASE}/${id}`);
  },
};

export default paymentsApiService;
